import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { syncFromURL } from "@/store/slices/userSelection/userSelection";
import { parseURLParams } from "@/utils/url";
import { buildContextKey } from "@/store/listeners/selectionListener";
import { updateScreenLevelSelection } from "@/store/slices/screenLevelSelection/screenLevelSelection";

/**
 * Single-value numeric fields in UserSelectionState that arrive as strings from the URL.
 * Multi-value array fields (brands, markets, segments, filters, secondaryBrands → number[])
 * are already handled by parseURLParams via getAll().
 * questionOptions → Record<string, number[]> is parsed by parseURLParams from "qKey-optId" strings.
 */
const NUMERIC_FIELDS = [
  "profileMetric",
  "profileGroup",
  "profileResponse",
] as const;

export const useURLToStore = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // parseURLParams already uses getAll() for:
    //   brands, markets, segments, filters, secondaryBrands → number[]
    //   questionOptions → Record<string, number[]> (parsed from "qKey-optId" strings)
    // and get() for all other single-value keys → string
    const parsed: Record<string, any> = parseURLParams(searchParams);

    // Derive module from first pathname segment.
    // Fall back to "VISIBILITY" (not "EQUITY") so that an initial load at "/"
    // does not override the userSelection initial state with the wrong module
    // before Home.tsx has had a chance to redirect to the correct first module.
    const module =
      location.pathname.split("/")?.[1] === "emotional"
        ? "IMAGERY"
        : location.pathname.split("/")?.[1]?.toUpperCase() || "VISIBILITY";

    const screenType = location.pathname?.split("/")?.[2] || "brand";

    // Cast single numeric fields string → number
    NUMERIC_FIELDS.forEach((key) => {
      if (parsed[key] !== undefined && parsed[key] !== "") {
        const n = Number(parsed[key]);
        parsed[key] = isNaN(n) ? null : n;
      }
    });

    // Combine ageStart / ageEnd into ageRange: number[]
    const ageStart = Number(parsed.ageStart);
    const ageEnd = Number(parsed.ageEnd);
    if (!isNaN(ageStart) && ageStart > 0 && !isNaN(ageEnd) && ageEnd > 0) {
      parsed.ageRange = [ageStart, ageEnd];
    }
    delete parsed.ageStart;
    delete parsed.ageEnd;

    // If filters or segments are absent from the URL, explicitly reset them to empty arrays.
    // filters are only a supported URL key on "brand" and "market" screen types;
    // for "segment" the key is not part of the URL schema so we must NOT overwrite it.
    if (
      (screenType === "brand" || screenType === "market") &&
      !searchParams.has("filters")
    ) {
      parsed.filters = [];
    }
    if (!searchParams.has("segments")) {
      parsed.segments = [];
    }

    dispatch(syncFromURL({ ...parsed, module, screenType } as any));

    // ── Snapshot current screen selection → screenLevelSelection ────────────
    // Fired on every URL commit (including initial load and navigateTo calls
    // from selectionListener).  Saves the URL-derived brands/markets/segments/
    // secondaryBrands so that ModeDropdown / screen switches can restore them.
    const contextKey = buildContextKey(
      screenType,
      (parsed.mode as string) ?? "POINT_IN_TIME",
      (parsed.visualizationType as string) ?? "",
    );
    dispatch(
      updateScreenLevelSelection({
        screenType: contextKey,
        value: {
          brands: (parsed.brands as number[]) ?? [],
          markets: (parsed.markets as number[]) ?? [],
          segments: (parsed.segments as number[]) ?? [],
          secondaryBrands: (parsed.secondaryBrands as number[]) ?? [],
        },
      }),
    );
  }, [location]);
};

export default useURLToStore;
